import { z } from "zod";
import { DataSource } from "./enums.js";

/** Versión del algoritmo de mapas/métricas. Cambiarla invalida cachés y queda en cada exportación. */
export const ALGORITHM_VERSION = "gazelab-heatmap-1.0.0";

/** Lado largo de la rejilla canónica de los mapas de calor (celdas). */
export const DEFAULT_GRID_LONG_SIDE = 160;
/** Sigma predeterminada como fracción del lado largo de la imagen. */
export const DEFAULT_SIGMA_FRACTION = 0.03;
/** Rejilla gruesa para la métrica de cobertura espacial. */
export const COVERAGE_GRID = 10;

export const AnalysisQuery = z
  .object({
    imageId: z.uuid(),
    groupA: z.uuid(),
    groupB: z.uuid(),
    source: DataSource.default("webgazer"),
    sigma: z.coerce.number().min(0.005).max(0.2).default(DEFAULT_SIGMA_FRACTION),
    gridLongSide: z.coerce.number().int().min(32).max(256).default(DEFAULT_GRID_LONG_SIDE),
    /** Si es true, incluye sesiones cuya validación fue `warn` (los `fail` sin override nunca entran). */
    includeWarnQuality: z
      .enum(["true", "false"])
      .default("true")
      .transform((v) => v === "true"),
  })
  .strict();
export type AnalysisQuery = z.infer<typeof AnalysisQuery>;

export const RoiGeometry = z
  .object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    width: z.number().gt(0).max(1),
    height: z.number().gt(0).max(1),
  })
  .strict()
  .refine((g) => g.x + g.width <= 1 + 1e-9 && g.y + g.height <= 1 + 1e-9, {
    message: "El rectángulo debe quedar dentro de la imagen (coordenadas normalizadas 0–1)",
  });
export type RoiGeometry = z.infer<typeof RoiGeometry>;

/** Intervalo de tiempo de un ROI de video (ms del video). `null` en ambos = todo el video. */
const RoiTime = z.number().int().min(0).max(600_000).nullable();
const validInterval = (r: { startMs?: number | null; endMs?: number | null }) =>
  r.startMs == null || r.endMs == null || r.endMs > r.startMs;
const INTERVAL_MESSAGE = { message: "El fin del intervalo debe ser posterior al inicio", path: ["endMs"] };

export const RoiCreate = z
  .object({
    studyImageId: z.uuid(),
    label: z.string().min(1).max(80),
    shape: z.literal("rect").default("rect"),
    geometry: RoiGeometry,
    startMs: RoiTime.optional(),
    endMs: RoiTime.optional(),
  })
  .strict()
  .refine(validInterval, INTERVAL_MESSAGE);
export type RoiCreate = z.infer<typeof RoiCreate>;

export const RoiPatch = z
  .object({
    label: z.string().min(1).max(80).optional(),
    geometry: RoiGeometry.optional(),
    startMs: RoiTime.optional(),
    endMs: RoiTime.optional(),
  })
  .strict()
  .refine(validInterval, INTERVAL_MESSAGE);
export type RoiPatch = z.infer<typeof RoiPatch>;

export interface RoiDto {
  id: string;
  studyImageId: string;
  label: string;
  shape: "rect";
  geometry: RoiGeometry;
  /** Solo video: intervalo en ms del video (null = desde el inicio / hasta el final). */
  startMs: number | null;
  endMs: number | null;
  createdAt: string;
}

/** Matriz densa fila-mayor: `values[row * width + col]`. */
export interface GridDto {
  width: number;
  height: number;
  values: number[];
  /** Máximo (heatmaps) o máximo absoluto (diferencia). */
  max: number;
}

export interface ExclusionCounts {
  /** Sesiones excluidas manualmente por administración. */
  manual: number;
  /** Sesiones sin validación aprobada ni override. */
  calibrationFailed: number;
  /** Sesiones con validación `warn` excluidas por filtro. */
  calibrationWarnFiltered: number;
  /** Sesiones sin ensayo completado para esta imagen. */
  noCompletedTrial: number;
  /** Ensayos con muestras válidas insuficientes. */
  insufficientValidSamples: number;
  /** Sesiones no completadas (abortadas/interrumpidas/en curso). */
  sessionNotCompleted: number;
}

export interface GroupAnalysisDto {
  groupId: string;
  groupCode: string;
  groupName: string;
  /** Participantes elegibles que aportan matriz. */
  n: number;
  excluded: ExclusionCounts;
  ageRange: { min: number; max: number } | null;
  samples: {
    raw: number;
    valid: number;
    onImage: number;
    offImage: number;
    invalid: number;
    /** Proporción de muestras válidas fuera de la imagen (sobre muestras con coordenadas válidas). */
    offImageRatio: number | null;
  };
  quality: {
    meanValidationErrorPx: number | null;
    meanValidationErrorRelative: number | null;
    meanValidSampleRatio: number | null;
    meanSamplesPerSecond: number | null;
    sessionsWithOverride: number;
  };
  /** Porcentaje medio de celdas de la rejilla gruesa (10×10) con al menos una muestra. */
  meanCoverage: number | null;
  heatmap: GridDto | null;
  rois: Array<{ roiId: string; label: string; meanFraction: number | null; sd: number | null }>;
}

export interface AnalysisResultDto {
  algorithmVersion: string;
  generatedAt: string;
  cached: boolean;
  params: {
    studyId: string;
    imageId: string;
    source: DataSource;
    sigma: number;
    gridLongSide: number;
    includeWarnQuality: boolean;
    minimumValidSampleRatio: number;
    minimumValidSamples: number;
  };
  image: { id: string; naturalWidth: number; naturalHeight: number; filenameOriginal: string };
  groupA: GroupAnalysisDto;
  groupB: GroupAnalysisDto;
  /** `heatmapA - heatmapB`; null si alguno de los grupos no tiene participantes. */
  difference: GridDto | null;
  /** Diferencia por ROI en puntos porcentuales (A − B). */
  roiDifferences: Array<{ roiId: string; label: string; diffPp: number | null }>;
  /** Escala compartida para A y B (máximo de ambos). */
  sharedMax: number;
  warnings: string[];
}

export const EXPLORATORY_WARNING =
  "Estimaciones con webcam: mapas exploratorios; no equivalen a un eye tracker clínico ni demuestran causalidad atribuible a la edad.";

/** Mínimo orientativo de participantes por grupo para no advertir de muestra muy pequeña. */
export const SMALL_GROUP_WARNING_N = 10;
